import { useEffect, useRef, useState } from 'react';

import { FOOD_TYPE, FoodType } from '../../../../../constants/foodType';
import { DatabaseFoodItem } from '../../../../../interfaces/DatabaseFoodItem';
import { FoodListItem } from '../../../../../interfaces/FoodListItem';
import { foodsService } from '../../../../../services/foodsService';
import {
  INGREDIENT_SEARCH_TAB,
  IngredientSearchTab,
} from '../constants/ingredientSearchTabs';
import { paginationSmallListLimit } from '../../../../../../shared/constants/systemConstants';

/** `null` - the Nutritionix tab searches `api/food/search`, not the own-food endpoints */
export const ingredientSearchTabFoodType = {
  [INGREDIENT_SEARCH_TAB.CORE]: FOOD_TYPE.CORE,
  [INGREDIENT_SEARCH_TAB.SUPPLEMENT]: FOOD_TYPE.SUPPLEMENT,
  [INGREDIENT_SEARCH_TAB.VEGGIES]: FOOD_TYPE.VEGGIES,
  [INGREDIENT_SEARCH_TAB.NUTRITIONIX]: null,
} satisfies Record<IngredientSearchTab, FoodType | null>;

type IngredientSearchFood = FoodListItem | DatabaseFoodItem;

type UseIngredientSearchReturn = {
  foods: IngredientSearchFood[];
  /** `foods` answers a typed query - false while browsing an own tab and before any search */
  isSearch: boolean;
  hasMore: boolean;
  /** bumped when a first page replaces `foods` (new query, cleared field), not on load-more - used as the
   * list scroll box `key` so new results open scrolled to the top */
  listKey: number;
  searchFoods: (query: string) => Promise<void>;
  /** Appends the next page of the current query; a no-op while any request is in flight or nothing is left */
  loadMoreFoods: () => Promise<void>;
};

/**
 * Bound to one tab for its whole life - remount it to switch tabs.
 * Browses an own tab's first page on mount; Nutritionix waits for a typed query and never pages.
 * Every request (first or next page) is reported through `onLoadingChange`.
 */
export const useIngredientSearch = (
  tab: IngredientSearchTab,
  onLoadingChange: (isLoading: boolean) => void,
): UseIngredientSearchReturn => {
  const [foods, setFoods] = useState<IngredientSearchFood[]>([]);
  const [isSearch, setIsSearch] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [listKey, setListKey] = useState(0);

  // the request in flight, `null` when idle - a response whose request is no longer here is dropped
  const requestRef = useRef<object | null>(null);
  // the last page that landed - load-more asks for the one after it
  const pageRef = useRef({ query: '', page: 0 });

  const foodType = ingredientSearchTabFoodType[tab];

  const request = async <T>(
    fetchFoods: () => Promise<T>,
    applyResponse: (response: T) => void,
  ) => {
    const token = {};
    requestRef.current = token;
    onLoadingChange(true);

    try {
      const response = await fetchFoods();

      if (requestRef.current === token) {
        applyResponse(response);
      }
    } finally {
      if (requestRef.current === token) {
        requestRef.current = null;
        onLoadingChange(false);
      }
    }
  };

  const replaceFoods = (newFoods: IngredientSearchFood[]) => {
    setFoods(newFoods);
    setListKey((prevKey) => prevKey + 1);
  };

  const fetchPage = (ownFoodType: FoodType, query: string, page: number) =>
    request(
      () =>
        foodsService.getFoods(
          ownFoodType,
          {
            filters: { name: query, excludeBanned: true },
            requestedPage: { page, size: paginationSmallListLimit },
          },
          { isRunGlobalSpinner: false },
        ),
      (response) => {
        pageRef.current = { query, page };
        if (page) {
          setFoods((prevFoods) => [...prevFoods, ...response.foods]);
        } else {
          replaceFoods(response.foods);
        }
        setHasMore(page + 1 < response.totalPages);
        setIsSearch(!!query);
      },
    );

  const searchFoods = async (query: string) => {
    if (foodType) {
      return fetchPage(foodType, query, 0);
    }

    // a Nutritionix search is a live third-party call that also writes the backend cache - none while empty
    if (!query) {
      requestRef.current = null;
      replaceFoods([]);
      setIsSearch(false);
      onLoadingChange(false);
      return;
    }

    return request(
      () =>
        foodsService.searchDatabaseFoods(FOOD_TYPE.CORE, query, {
          isRunGlobalSpinner: false,
        }),
      (response) => {
        replaceFoods(response.foods);
        setIsSearch(true);
      },
    );
  };

  const loadMoreFoods = async () => {
    if (!foodType || !hasMore || requestRef.current) return;

    const { query, page } = pageRef.current;

    return fetchPage(foodType, query, page + 1);
  };

  useEffect(() => {
    searchFoods('');

    // a late response of an unmounted panel must not clear the spinner the next panel reports
    return () => {
      requestRef.current = null;
    };
  }, []);

  return { foods, isSearch, hasMore, listKey, searchFoods, loadMoreFoods };
};
